import React from 'react';
import { NavLink } from 'react-router-dom';
import { Users, TrendingUp, ShieldCheck, LogOut, Building2 } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';

// Pages in the order they appear in the top bar. `allowed` decides who sees each link;
// the same rules guard the routes in App.js and the API on the backend.
export const ADMIN_PAGES = [
  { path: '/', label: 'Users', icon: Users, allowed: ({ can }) => can('users') },
  { path: '/growth', label: 'Growth', icon: TrendingUp, allowed: ({ can }) => can('growth') },
  { path: '/enterprise', label: 'Enterprise Inquiries', icon: Building2, allowed: ({ can }) => can('enterprise') },
  { path: '/team', label: 'Team', icon: ShieldCheck, allowed: ({ isOwner }) => isOwner },
];

export default function AdminNav() {
  const { admin, can, isOwner, logout } = useAdmin();
  const pages = ADMIN_PAGES.filter((p) => p.allowed({ can, isOwner }));

  return (
    <header className="sticky top-0 z-40 bg-[#070a13]/80 backdrop-blur-md border-b border-white/5 px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-4 min-w-0">
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 bg-[#6c4ff6] rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 40 40" width="18" height="18" fill="none">
              <rect x="3" y="5" width="23" height="16" rx="5" fill="#BFFF4A" />
              <path d="M9 21 L6 28 L15 21Z" fill="#BFFF4A" />
              <rect x="14" y="16" width="23" height="16" rx="5" fill="white" fillOpacity=".9" />
            </svg>
          </div>
          <span className="font-syne font-bold text-xl tracking-tight hidden sm:inline">RYZZ</span>
        </div>

        <nav className="flex items-center gap-1 overflow-x-auto">
          {pages.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border transition-all duration-150 whitespace-nowrap ${
                  isActive
                    ? 'bg-indigo-500/10 border-indigo-500/30 text-white'
                    : 'bg-slate-900/40 border-white/5 text-slate-400 hover:text-white hover:border-indigo-500/30'
                }`
              }
            >
              <Icon className="w-4 h-4" /> {label}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-2.5 bg-slate-900/60 border border-white/5 py-1.5 pl-2.5 pr-4 rounded-xl">
          <div className="w-7 h-7 rounded-lg bg-indigo-500 flex items-center justify-center font-bold text-white text-xs">
            {admin?.avatar || 'AD'}
          </div>
          <div className="hidden sm:block text-left">
            <span className="text-xs font-semibold text-slate-200 block">{admin?.name || 'Admin'}</span>
            <span className="text-[10px] text-slate-500 block">{isOwner ? 'Owner' : 'Team member'}</span>
          </div>
        </div>
        <button
          onClick={logout}
          className="p-2.5 rounded-xl bg-slate-900/40 border border-white/5 hover:border-rose-500/30 hover:bg-rose-500/5 text-slate-400 hover:text-rose-400 transition-all duration-150 group"
          title="Sign out"
        >
          <LogOut className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </header>
  );
}
